/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Column, Events, GridApi, ICellRendererComp, ICellRendererParams, RowNode } from 'ag-grid-community';
import { constant, property } from 'lodash';

const GROUP_TITLE_AFFECTING_GRID_EVENTS = [Events.EVENT_MODEL_UPDATED];

/**
 * Cell renderer for group title. This renderer allows to display title on the top of each row group.
 * If sorting was changed or grid data was updated, group title can move from one row to another one.
 *
 * Each separate row group is defined by <code>getGroupKey</code> function.
 * <code>isGrouped</code> defines if row belongs to some row group.
 */
export class GroupTitleCellRenderer implements ICellRendererComp {
  private api: GridApi;
  private column: Column;
  private node: RowNode;
  private eTitle: HTMLElement;
  private visible = true;
  private groupKey: any;
  private getGroupKey: TransformFn<any, any>;
  private isGrouped: PredicateFn<any>;

  constructor() {
    this.updateTitleVisibility = this.updateTitleVisibility.bind(this);
  }

  init(params: ICellRendererParams): void {
    this.api = params.api;
    this.column = params.column;
    this.node = params.node;
    this.getGroupKey = params['getGroupKey'] || property(params.colDef.field || '');
    this.isGrouped = params['isGrouped'] || constant(true);

    this.eTitle = document.createElement('div');
    this.refresh(params);

    GROUP_TITLE_AFFECTING_GRID_EVENTS.forEach((eventName) => {
      this.api.addEventListener(eventName, this.updateTitleVisibility);
    });
  }

  destroy(): void {
    GROUP_TITLE_AFFECTING_GRID_EVENTS.forEach((eventName) => {
      this.api.removeEventListener(eventName, this.updateTitleVisibility);
    });
  }

  getGui(): HTMLElement {
    return this.eTitle;
  }

  refresh(params: any): boolean {
    this.eTitle.textContent = params.valueFormatted ? params.value : params.value;
    this.groupKey = this.getGroupKey(this.node.data);
    this.updateTitleVisibility();
    return true;
  }

  /**
   * Updates visibility of cell content
   */
  private updateTitleVisibility(): void {
    if (!this.isGrouped(this.node.data)) {
      return;
    }

    const shouldBeVisibleNow = this.isShouldBeVisibleNow();
    if (shouldBeVisibleNow !== this.visible) {
      this.eTitle.style.display = shouldBeVisibleNow ? 'block' : 'none';
      this.visible = shouldBeVisibleNow;
    }
  }

  /**
   * Group title is visible if it is the first row of grid or if previous row does not belong to the same row group.
   */
  private isShouldBeVisibleNow(): boolean {
    const { rowIndex } = this.node;
    if (rowIndex === 0) {
      return true;
    }
    const prevObject = this.api.getModel().getRow(rowIndex - 1);
    const previousData =  !!prevObject ? prevObject.data : null;
    return !this.isGrouped(previousData) || this.getGroupKey(previousData) !== this.groupKey;
  }
}
